import {
  Component,
  computed,
  ContentChild,
  effect,
  ElementRef,
  input,
  output,
  TemplateRef,
  viewChildren,
} from '@angular/core';
import { Checkbox } from 'primeng/checkbox';
import { ReactiveFormsModule } from '@angular/forms';
import { DynamicFormFieldComponent } from '../fields/dynamic-form-field.component';
import { NgClass, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { DynamicForm } from '../../service/dynamic-form.service';
import {
  BinaryFormModel,
  FormModel,
  MultiFormModel,
  SimpleFormModel,
} from '../../model/dynamic-form';
import { TooltipDirective } from '@vydaps/gui-commons';
import { KeyInfo } from '../../directives/key-info.directive';

@Component({
  selector: 'shared-ui-dynamic-form',
  imports: [
    Checkbox,
    ReactiveFormsModule,
    DynamicFormFieldComponent,
    NgTemplateOutlet,
    TooltipDirective,
    KeyInfo,
    NgOptimizedImage,
    NgClass,
  ],
  templateUrl: './dynamic-form.component.html',
  styleUrl: './dynamic-form.component.scss',
})
export class DynamicFormComponent {
  readonly showHeader = input<boolean>(true);
  readonly headerIcon = input<string | undefined>();
  readonly inputWidth = input<string>('w-full');

  readonly dynamicForm = input.required<DynamicForm>();

  @ContentChild('header') headerTemplate?: TemplateRef<unknown>;
  @ContentChild('textinput') textinputTemplate?: TemplateRef<unknown>;
  @ContentChild('textarea') textareaTemplate?: TemplateRef<unknown>;
  @ContentChild('dropdown') dropdownTemplate?: TemplateRef<unknown>;
  @ContentChild('date') dateTemplate?: TemplateRef<unknown>;
  @ContentChild('checkbox') checkboxTemplate?: TemplateRef<unknown>;
  @ContentChild('switch') switchTemplate?: TemplateRef<unknown>;
  @ContentChild('radio') radioTemplate?: TemplateRef<unknown>;

  formFieldMapChanged = output<Record<string, ElementRef>>();

  private readonly formFields = viewChildren(DynamicFormFieldComponent, { read: ElementRef });
  private readonly formFieldMap = computed(() => {
    return this.formFields().reduce(
      (map, field) => {
        const key = field.nativeElement.getAttribute('data-key');
        if (key) {
          map[key] = field;
        }
        return map;
      },
      {} as Record<string, ElementRef>,
    );
  });

  constructor() {
    effect(() => {
      this.formFieldMapChanged.emit(this.formFieldMap());
    });
  }

  getAsSimpleFormModel(formModel: FormModel): SimpleFormModel {
    return formModel as SimpleFormModel;
  }

  getAsBinaryFormModel(formModel: FormModel): BinaryFormModel {
    return formModel as BinaryFormModel;
  }

  getAsMultiFormModel(formModel: FormModel): MultiFormModel {
    return formModel as MultiFormModel;
  }
}
